import type {
  MarkContext,
  MarkPageContext,
  MarkPageContextCell,
  MarkPageContextColumn,
  MarkPageContextFilter,
  MarkPageContextSelection,
  MarkPageContextTable,
} from 'containers/admin/clients/client/tabs-components/ai-assistant/mark-panel/mark-panel.types';

import { COMPARE_TO_OPTIONS } from 'containers/user/critical-elements-monitoring/components/filters-bar';
import {
  BAR_PAIR_LABELS,
  EXTEND_WITH_BASE_LABEL,
  EXTEND_WITH_PRODUCT_LABEL,
  isFunnelRowCohortUnavailable,
  STAGE_BASE_LABEL,
} from 'containers/user/critical-elements-monitoring/monitoring-constants';
import type { StageDisplayMode } from 'containers/user/critical-elements-monitoring/monitoring-constants';
import type { MonitoringMarkSnapshot } from 'containers/user/critical-elements-monitoring/mark/monitoring-mark-context';
import { MONITORING_MARK_SECTION } from 'containers/user/critical-elements-monitoring/mark/monitoring-mark-context';
import type {
  CompareTo,
  ExtendWithOption,
  FunnelSection,
  FunnelStageKey,
} from 'containers/user/critical-elements-monitoring/critical-elements-monitoring.types';

const MONITORING_PAGE: MarkPageContext['page'] = {
  id: 'monitoring',
  title: 'Monitoring',
  description:
    'Enrollment funnel monitoring for the selected entering class: inquiry, ' +
    'application, admit and net deposit volumes and rates for the whole ' +
    'class and for each Critical Element, compared with the same point in ' +
    'the prior cycle or the prior year final.',
};

const MONITORING_DEFINITIONS = [
  {
    term: 'Critical Element',
    definition:
      'A student population the school tracks against a goal, defined by ' +
      'one or more dimensions such as geography, income band or ' +
      'application status.',
  },
];

const TOTAL_FUNNEL_TABLE_ID = 'total_funnel';

const FUNNEL_TABLE_GRAIN = 'One row per funnel stage';

const buildCriticalElementTableId = (entityId: string): string =>
  `critical_element_${entityId}`;

const DISPLAY_MODE_LABEL: Record<StageDisplayMode, string> = {
  volume: 'Volume',
  rate: 'Rate',
  proportion: 'Proportion',
};

type FunnelRowKey = FunnelStageKey | ExtendWithOption;

const FUNNEL_STAGE_ORDER: readonly FunnelStageKey[] = [
  'inquiry',
  'application',
  'admit',
  'netDeposit',
];

const getFunnelRowSources = (
  funnel: FunnelSection
): Array<{
  key: FunnelRowKey;
  label: string;
  volume: FunnelSection['stages'][number]['volume'];
  rate: FunnelSection['stages'][number]['rate'];
  proportion: FunnelSection['stages'][number]['proportion'];
  comparators: FunnelSection['stages'][number]['comparators'];
  cohortAvailable?: boolean;
}> => {
  const stageByKey = new Map(
    funnel.stages.map((stage) => [stage.stageKey, stage])
  );

  const stageRows = FUNNEL_STAGE_ORDER.map((key) => stageByKey.get(key))
    .filter((stage): stage is FunnelSection['stages'][number] => Boolean(stage))
    .map((stage) => ({
      key: stage.stageKey,
      label: STAGE_BASE_LABEL[stage.stageKey],
      volume: stage.volume,
      rate: stage.rate,
      proportion: stage.proportion,
      comparators: stage.comparators,
      cohortAvailable: stage.cohortAvailable,
    }));

  const extensionRows = funnel.extensions.map((extension) => ({
    key: extension.type,
    label: EXTEND_WITH_BASE_LABEL[extension.type],
    volume: extension.volume,
    rate: extension.rate,
    proportion: extension.proportion,
    comparators: extension.comparators,
  }));

  return [...stageRows, ...extensionRows];
};

const buildFunnelTableColumns = (
  compareTo: CompareTo,
  cohortLabel: string
): MarkPageContextColumn[] => {
  const barLabels = BAR_PAIR_LABELS[compareTo];

  return [
    { key: 'stage', label: 'Stage', type: 'string' },
    {
      key: 'volume',
      label: barLabels.current,
      type: 'number',
      format: 'integer',
    },
    {
      key: 'volume_prior',
      label: barLabels.prior,
      type: 'number',
      format: 'integer',
    },
    {
      key: 'volume_change_pct',
      label: 'Volume change, My School (YoY)',
      type: 'number',
      format: 'percent',
    },
    {
      key: 'cohort_volume_change_pct',
      label: `Volume change, ${cohortLabel} (YoY)`,
      type: 'number',
      format: 'percent',
    },
    { key: 'rate', label: 'Rate', type: 'number', format: 'percent' },
    {
      key: 'rate_change_pp',
      label: 'Rate change, My School (YoY)',
      type: 'number',
      format: 'decimal',
    },
    {
      key: 'cohort_rate_change_pp',
      label: `Rate change, ${cohortLabel} (YoY)`,
      type: 'number',
      format: 'decimal',
    },
    {
      key: 'proportion',
      label: 'Proportion',
      type: 'number',
      format: 'percent',
    },
    {
      key: 'proportion_change_pp',
      label: 'Proportion change, My School (YoY)',
      type: 'number',
      format: 'decimal',
    },
    {
      key: 'cohort_rate',
      label: 'Cohort Rate',
      type: 'number',
      format: 'percent',
    },
    {
      key: 'vs_cohort_pp',
      label: 'vs Cohort',
      type: 'number',
      format: 'decimal',
    },
  ];
};

const buildFunnelTable = (
  id: string,
  title: string,
  funnel: FunnelSection,
  compareTo: CompareTo,
  cohortLabel: string
): MarkPageContextTable => {
  const rows: MarkPageContextCell[][] = getFunnelRowSources(funnel).map(
    (source) => {
      const volumeCohortHidden = isFunnelRowCohortUnavailable(
        source.key,
        'volume',
        source.cohortAvailable
      );
      const rateCohortHidden = isFunnelRowCohortUnavailable(
        source.key,
        'rate',
        source.cohortAvailable
      );

      return [
        source.label,
        source.volume.current,
        source.volume.previous,
        source.volume.deltaPct,
        volumeCohortHidden ? null : source.comparators.cohort ?? null,
        source.rate.current,
        source.rate.pp ?? null,
        rateCohortHidden ? null : source.comparators.cohortPp ?? null,
        source.proportion.current,
        source.proportion.pp ?? null,
        rateCohortHidden ? null : source.comparators.cohortRateCurrent ?? null,
        rateCohortHidden ? null : source.comparators.vsCohortPp ?? null,
      ];
    }
  );

  return {
    id,
    title,
    grain: FUNNEL_TABLE_GRAIN,
    columns: buildFunnelTableColumns(compareTo, cohortLabel),
    rows,
    totals: [],
    total_row_count: rows.length,
    truncated: false,
  };
};

const buildMonitoringFilters = (
  snapshot: MonitoringMarkSnapshot
): MarkPageContextFilter[] => {
  const compareToLabel = COMPARE_TO_OPTIONS.find(
    (option) => option.value === snapshot.compareTo
  )?.label;

  const filters: MarkPageContextFilter[] = [
    { label: 'Entering class', values: [String(snapshot.enteringClass)] },
  ];

  if (compareToLabel) {
    filters.push({ label: 'Change in time', values: [compareToLabel] });
  }

  if (snapshot.cohortLabel) {
    filters.push({ label: 'Cohort', values: [snapshot.cohortLabel] });
  }

  if (snapshot.extend.length) {
    filters.push({
      label: 'Extend with',
      values: snapshot.extend.map(
        (option) => EXTEND_WITH_PRODUCT_LABEL[option]
      ),
    });
  }

  filters.push({
    label: 'Display',
    values: [DISPLAY_MODE_LABEL[snapshot.displayMode]],
  });

  if (snapshot.ceDisplayMode !== snapshot.displayMode) {
    filters.push({
      label: 'Critical Elements display',
      values: [DISPLAY_MODE_LABEL[snapshot.ceDisplayMode]],
    });
  }

  return filters;
};

const DISPLAY_MODE_COLUMN_KEY: Record<StageDisplayMode, string> = {
  volume: 'volume',
  rate: 'rate',
  proportion: 'proportion',
};

const resolveDisplayModeColumnKey = (displayMode: StageDisplayMode): string =>
  DISPLAY_MODE_COLUMN_KEY[displayMode];

const resolveTileSelectionDisplayMode = (
  context: Extract<MarkContext, { kind: 'tile' }>,
  snapshot: MonitoringMarkSnapshot
): StageDisplayMode =>
  context.sectionId === MONITORING_MARK_SECTION.criticalElement
    ? snapshot.ceDisplayMode
    : snapshot.displayMode;

const resolveSelectionTableId = (context: MarkContext): string | null => {
  if (context.kind === 'tile') {
    return context.sectionId === MONITORING_MARK_SECTION.criticalElement &&
      context.entityId
      ? buildCriticalElementTableId(context.entityId)
      : TOTAL_FUNNEL_TABLE_ID;
  }

  if (context.kind === 'entity' && context.entityType === 'criticalElement') {
    return buildCriticalElementTableId(context.entityId);
  }

  return null;
};

const buildSelection = (
  context: MarkContext,
  snapshot: MonitoringMarkSnapshot,
  funnelsByTableId: Map<string, FunnelSection>
): MarkPageContextSelection | undefined => {
  const tableId = resolveSelectionTableId(context);

  if (!tableId) return undefined;

  if (context.kind === 'entity') {
    return { table_id: tableId, row_index: 0, column_key: 'volume' };
  }

  if (context.kind === 'tile') {
    const funnel = funnelsByTableId.get(tableId);

    if (!funnel) return undefined;

    const rowIndex = getFunnelRowSources(funnel).findIndex(
      (source) => source.key === context.tileId
    );

    if (rowIndex === -1) return undefined;

    return {
      table_id: tableId,
      row_index: rowIndex,
      column_key: resolveDisplayModeColumnKey(
        resolveTileSelectionDisplayMode(context, snapshot)
      ),
    };
  }

  return undefined;
};

export const buildMonitoringPageContext = (
  context: MarkContext,
  snapshot: MonitoringMarkSnapshot
): MarkPageContext | null => {
  const { metrics } = snapshot;

  if (!metrics) return null;

  const funnelsByTableId = new Map<string, FunnelSection>([
    [TOTAL_FUNNEL_TABLE_ID, metrics.total],
  ]);
  const titleByTableId = new Map<string, string>([
    [TOTAL_FUNNEL_TABLE_ID, 'Total funnel'],
  ]);

  snapshot.criticalElements.forEach((criticalElement) => {
    const funnel = metrics.byCriticalPopulationId[criticalElement.id];

    if (!funnel) return;

    const tableId = buildCriticalElementTableId(criticalElement.id);

    funnelsByTableId.set(tableId, funnel);
    titleByTableId.set(tableId, criticalElement.name);
  });

  const tables = Array.from(funnelsByTableId.entries()).map(([id, funnel]) =>
    buildFunnelTable(
      id,
      titleByTableId.get(id) ?? '',
      funnel,
      snapshot.compareTo,
      snapshot.cohortLabel
    )
  );

  return {
    schema_version: '1',
    captured_at: new Date().toISOString(),
    page: MONITORING_PAGE,
    filters: buildMonitoringFilters(snapshot),
    tables,
    selection: buildSelection(context, snapshot, funnelsByTableId),
    definitions: MONITORING_DEFINITIONS,
  };
};
